/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, DragEvent, useEffect, useRef } from 'react';
import clsx from 'clsx';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UIChartsIcon } from '@futrli/ui-components';
import Textbox from 'carbon-react/lib/components/textbox';

import { APP_WIDGET_NAMES, CARD_TYPES } from '../../types';
import { useIsExcelOrganisation } from '../../../predict-management/hooks/useIsExcelOrganisation';
import { usePredictManagementContext } from '../../../predict-management/context/predict-management-context';

import styles from './widgets-panel.scss';
import sidebarStyles from './sidebar.scss';

export const WidgetsPanel = ({
  onItemDragStart
}: {
  onItemDragStart: (
    e: DragEvent<HTMLDivElement | HTMLSpanElement>,
    type: CARD_TYPES,
    data?: Record<string, unknown>
  ) => void;
}) => {
  const intl = useIntl();
  const appWidgetNames = useRef([
    APP_WIDGET_NAMES.CASH_IN_FROM_INVOICES,
    APP_WIDGET_NAMES.CASH_OUT_FROM_BILLS,
    APP_WIDGET_NAMES.TOP_INVOICES_DUE,
    APP_WIDGET_NAMES.TOP_BILLS_OWED,
    APP_WIDGET_NAMES.TOP_SALES_ACCOUNTS,
    APP_WIDGET_NAMES.TOP_COST_AND_EXPENSES_ACCOUNTS,
    APP_WIDGET_NAMES.ACCOUNT_BRINGING_MOST_CASH,
    APP_WIDGET_NAMES.ACCOUNTS_GREATEST_CASH_EXPENDITURE,
    APP_WIDGET_NAMES.CUSTOMERS_PAID_MOST,
    APP_WIDGET_NAMES.SUPPLIERS_YOU_PAID_MOST,
    APP_WIDGET_NAMES.TOP_DEBT_BALANCES,
    APP_WIDGET_NAMES.RATIOS,
    APP_WIDGET_NAMES.CUSTOMERS_AND_SUPPLIERS
  ]);

  const excelAppWidgetNames = useRef([
    APP_WIDGET_NAMES.TOP_SALES_ACCOUNTS,
    APP_WIDGET_NAMES.TOP_COST_AND_EXPENSES_ACCOUNTS,
    APP_WIDGET_NAMES.TOP_DEBT_BALANCES,
    APP_WIDGET_NAMES.RATIOS
  ]);

  const isExcelOrganisation = useIsExcelOrganisation();
  const { isFrenchAccounting } = usePredictManagementContext();

  const [searchedWidget, setSearchedWidget] = useState('');
  const [displayedWidgets, setDisplayedWidgets] = useState(appWidgetNames.current);

  const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchedWidget(value);
  };

  useEffect(() => {
    if (isExcelOrganisation) {
      setDisplayedWidgets(
        excelAppWidgetNames.current
          .filter(widget => {
            if (isFrenchAccounting && widget === APP_WIDGET_NAMES.RATIOS) {
              return false;
            }
            return true;
          })
          .filter(e => e.toLowerCase().split('-').join(' ').includes(searchedWidget.toLowerCase()))
      );
    } else {
      setDisplayedWidgets(
        appWidgetNames.current.filter(e => e.toLowerCase().split('-').join(' ').includes(searchedWidget.toLowerCase()))
      );
    }
  }, [searchedWidget, appWidgetNames, isExcelOrganisation, isFrenchAccounting]);

  return (
    <div className={clsx([sidebarStyles.sidebarItemContainer, styles.sidebarItemContainer])}>
      <Textbox
        placeholder={intl.formatMessage({ id: 'reporting.search_widgets', defaultMessage: 'Search widgets...' })}
        value={searchedWidget}
        inputIcon="search"
        onChange={handleSearchOnChange}
      />

      <div className={clsx(styles.sidebarItemContent)}>
        <div>
          <h2 className={clsx(styles.sectionHeader)}>
            <FormattedMessage id="reporting.community_widgets" defaultMessage="Community widgets" />
          </h2>
          <div className={clsx(sidebarStyles.sidebarItemContainer, styles.appWidgetItemsContainer)}>
            {displayedWidgets.map(appWidget => (
              <div
                key={appWidget}
                className={clsx(sidebarStyles.draggableWidget, styles.appWidgetItem)}
                draggable
                onDragStart={e => onItemDragStart(e, CARD_TYPES.APP_WIDGET, { name: appWidget })}
              >
                <UIChartsIcon iconName={appWidget} />
              </div>
            ))}
          </div>
          <div style={{ height: 52 }} />
        </div>
      </div>
    </div>
  );
};
